
import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { buildCategoryFilterData, buildLegendSyncData, matchValue, MatchMode } from '../../utils/filter.utils';
import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

export interface ClusteredBarSeries {
  name: string;
  data: number[];
  color?: string;
}

@Component({
  selector: 'app-clustered-bar',
  standalone: true,
  imports: [CommonModule, CapitalCasePipe],
  templateUrl: './clustered-bar-chart.component.html',
  styleUrls: ['./clustered-bar-chart.component.scss']
})
export class ClusteredBarComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('chartContainer', { static: false })
  chartContainer!: ElementRef<HTMLDivElement>;

  @Input() data!: any; // config from renderer
  @Input() categories: string[] = [];
  @Input() series: ClusteredBarSeries[] = [];
  @Input() showLegend = true;
  @Input() showPercentage = true;

  chartTitle: string = '';

  protected filterSub?: Subscription;
  protected unSubscribe$ = new Subject<void>();
  protected selectedSeriesName: string | null = null;
  protected selectedCategory: string | null = null;
  selectedCategories: string[] = [];
  selectedLegends: string[] = [];

  protected chart!: echarts.ECharts;
  protected lastRawBar: any[] = [];
  private resizeObserver!: ResizeObserver;
  protected responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  protected reflowSub?: Subscription;
  private themeSub?: Subscription;
  // Scroll offset for visible window
  scrollOffset = 0;
  maxVisibleCategories = 5;
  visibleCategories: string[] = [];
  // optional secondary labels per category (for right-side yAxis)
  secondaryLabels: string[] = [];
  visibleHeight = 250;
  // row height in px for each category row inside chart
  rowHeight = 80;
  // reserved label and scrollbar widths (pixels) so chart grid can accommodate them
  labelWidth = 40;
  scrollbarWidth = 12;
  // Scrollbar drag state
  protected isScrollbarDragging = false;
  protected scrollbarStartY = 0;
  protected scrollStartOffset = 0;
  protected dragHandler: ((e: MouseEvent) => void) | null = null;
  protected upHandler: (() => void) | null = null;
  // expose Math to template for expressions like Math.min(...)
  readonly Math = Math;

  constructor(
    protected configService: ConfigService,
    protected globalFilterService: GlobalFilterService,
    protected chartFilterHandler: ChartFilterHandlerService
  ) {}

  ngAfterViewInit(): void {
    console.log('ClusteredBar: ngAfterViewInit, chartContainer element:', this.chartContainer?.nativeElement);
    
    this.resizeObserver = new ResizeObserver(() => {
      this.chart?.resize();
    });

    if (!this.chartContainer) {
      console.warn('ClusteredBar: chartContainer not found, delaying initialization');
      setTimeout(() => this.initializeChart(), 100);
      return;
    }
    this.syncVisibleHeightFromContainer();
    this.initializeChart();
  }

  // Safely extract a value from object with case-insensitive key matching
  protected safeGetValue(obj: any, key: string): any {
    if (!obj || !key) return undefined;
    if (key in obj) return obj[key];
    const lower = key.toLowerCase();
    for (const k of Object.keys(obj)) {
      if (k.toLowerCase() === lower) return obj[k];
    }
    return undefined;
  }

  // Build secondary label values aligned with categories array from raw data
  protected buildSecondaryLabels(rawData: any[], categories: string[], categoryKey: string, secondaryKey: string): string[] {
    const labels: string[] = [];
    categories.forEach((cat) => {
      const item = rawData.find(d => String(this.safeGetValue(d, categoryKey) ?? '') === cat);
      const val = item ? this.safeGetValue(item, secondaryKey) : undefined;
      labels.push(val === undefined || val === null ? '' : String(val));
    });
    return labels;
  }

  protected initializeChart(): void {
    if (!this.chartContainer) {
      console.error('ClusteredBar: chartContainer still not available');
      return;
    }
    this.chart = echarts.init(this.chartContainer.nativeElement, 'dvm');
    this.resizeObserver.observe(this.chartContainer.nativeElement);
    console.log('ClusteredBar: Chart instance initialized:', this.chart);
    setTimeout(() => this.chart?.resize(), 0);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.syncVisibleHeightFromContainer();
      this.chart?.resize();
    });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init so ECharts picks up the updated 'dvm' registered theme.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.chart || !this.categories?.length) return;
      const el = this.chartContainer?.nativeElement;
      if (!el) return;
      this.chart.dispose();
      this.chart = echarts.init(el, 'dvm');
      this.renderChart();
    });

    this.filterSub = this.globalFilterService.filter$
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((filters: any) => {
        if (!this.chartFilterHandler.shouldReload(filters, this.data?.widgetId)) {
          // Handle legendSync (visual dimming from other charts)
          if (filters?.legendSync) {
            this.handleExternalLegendSync(filters);
          }
          return;
        }

        // Handle legendSync + reload
        if (filters?.legendSync) {
          this.handleExternalLegendSync(filters);
        }

        // Only clear selection on global dropdown change (no widgetId)
        if (!filters?.widgetId) {
          this.selectedCategories = [];
          this.selectedLegends = [];
        }

        this.loadAndRenderChart(filters);
      });
    this.loadAndRenderChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.chart) {
      if (changes['data']) {
        this.loadAndRenderChart();
      } else {
        this.renderChart();
      }
    }
  }

  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    this.filterSub?.unsubscribe();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    this.chart?.dispose();
    this.resizeObserver?.disconnect();
  }

  protected loadAndRenderChart(filters?: any): void {
    // Read showLegend configuration from JSON (chartConfig.showLegend or top-level showLegend)
    this.showLegend = (this.data?.chartConfig && typeof this.data.chartConfig.showLegend !== 'undefined')
      ? Boolean(this.data.chartConfig.showLegend)
      : (typeof this.data?.showLegend !== 'undefined' ? Boolean(this.data.showLegend) : this.showLegend);
    this.syncVisibleHeightFromContainer();
    this.chart?.resize();

    console.log('ClusteredBar: loadAndRenderChart called, data=', this.data, 'filters=', filters);
    if (!this.data || !this.data.apiUrl) {
      console.warn('ClusteredBar: No apiUrl provided');
      this.renderChart();
      return;
    }

    this.chart.showLoading();
    console.log('ClusteredBar: calling getConfig with apiUrl=', this.data.apiUrl, 'data=', this.data);
    this.configService.getConfig(this.data.apiUrl, this.data, filters).subscribe({
      next: (res: any) => {
        console.log('ClusteredBar: config response=', res);
        const payload = res?.data || res;
        
        // Extract title from response if available
        if (payload?.title) {
          this.chartTitle = payload.title;
        }
        
        // Handle flat array response (standard /spriced/platform/ format)
        const flatRows = this.extractFlatRows(res);
        // Handle response with bar property (raw data array)
        if (payload?.bar && Array.isArray(payload.bar)) {
          console.log('ClusteredBar: transforming bar data...');
          // keep raw payload rows for richer tooltip content (e.g., quantityKey)
          this.lastRawBar = payload.bar;
          const transformed = this.transformData(payload.bar, this.data.categoryKey, this.data.seriesKey, this.data.valueKey);
          this.categories = transformed.category;
          this.series = transformed.series;
          // Build secondary labels if configured
          if (this.data?.secondaryLabelKey) {
            this.secondaryLabels = this.buildSecondaryLabels(payload.bar, this.categories, this.data.categoryKey, this.data.secondaryLabelKey);
          } else {
            this.secondaryLabels = [];
          }
          // reset scroll offset on new data
          this.scrollOffset = 0;
          this.maxVisibleCategories = this.data?.chartConfig?.maxVisibleCategories || 5;
        } else if (payload?.categories && payload?.series) {
          // Handle pre-formatted response
          this.categories = payload.categories;
          this.series = payload.series;
          // use provided secondaryLabels if present in payload, otherwise reset
          if (Array.isArray(payload.secondaryLabels)) {
            this.secondaryLabels = payload.secondaryLabels;
          } else {
            this.secondaryLabels = [];
          }
          this.scrollOffset = 0;
          this.maxVisibleCategories = this.data?.chartConfig?.maxVisibleCategories || 5;
        } else if (flatRows.length > 0 && this.data?.categoryKey && this.data?.seriesKey && this.data?.valueKey) {
          this.lastRawBar = flatRows;
          const transformed = this.transformData(flatRows, this.data.categoryKey, this.data.seriesKey, this.data.valueKey);
          this.categories = transformed.category;
          this.series = transformed.series;
          if (this.data?.secondaryLabelKey) {
            this.secondaryLabels = this.buildSecondaryLabels(flatRows, this.categories, this.data.categoryKey, this.data.secondaryLabelKey);
          } else {
            this.secondaryLabels = [];
          }
          this.scrollOffset = 0;
          this.maxVisibleCategories = this.data?.chartConfig?.maxVisibleCategories || 5;
        } else {
          this.categories = [];
          this.series = [];
        }
        
            // Assign colors using chartConfig keys if provided
            const themeForPalette = this.themeService.resolve(this.data?.themeOverrides);
            const defaultPalette = themeForPalette.chart?.palette || ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'];
            const topNColors: string[] = this.data?.chartConfig?.topNColorsByStackPosition || [];
            const colorMap: { [k: string]: string } = this.data?.chartConfig?.colorMap || {};
            const colors = this.data?.colors || defaultPalette;
            const getSeriesColor = (name: string, idx: number) => {
              if (topNColors && topNColors.length > idx && topNColors[idx]) return topNColors[idx];
              if (colorMap && colorMap[name]) return colorMap[name];
              return colors[idx % colors.length];
            };
            this.series = this.series.map((s, i) => ({ ...s, color: s.color || getSeriesColor(s.name, i) }));
        
        this.renderChart();
        this.chart.hideLoading();
      },
      error: (err: any) => {
        console.error('ClusteredBar: config error', err);
        this.chart.hideLoading();
      }
    });
  }

  protected renderChart(): void {
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    console.log('ClusteredBar: renderChart called with categories=', this.categories, 'series=', this.series);
    if (!this.categories.length || !this.series.length) {
      console.warn('ClusteredBar: No categories or series data, clearing chart');
      try {
        this.chart.clear();
      } catch (e) {
        console.error('ClusteredBar: error clearing chart', e);
      }
      return;
    }

    // Calculate visible categories and offset based on scroll
    const maxVis = Math.min(this.maxVisibleCategories, this.categories.length);

// 👇 page based offset instead of sliding offset
const page = Math.floor(this.scrollOffset / this.maxVisibleCategories);
const startIdx = page * this.maxVisibleCategories;
    const endIdx = Math.min(startIdx + maxVis, this.categories.length);
    this.visibleCategories = this.categories.slice(startIdx, endIdx);

    const visibleSeries = this.series.map(s => ({
      ...s,
      data: s.data.slice(startIdx, endIdx)
    }));

    console.log('ClusteredBar: Rendering with visible categories:', this.visibleCategories.length, 'offset:', startIdx);

    // Font scale factor for large / TV screens
    const fs = this.responsiveLayoutService.getFontScale();
    const sc = (px: number) => Math.round(px * fs);

    const showPercentage = this.showPercentage;
    // compute legend layout: center when all legend items fit inside chart width
    const chartWidth = (this.chart && (this.chart.getWidth ? this.chart.getWidth() : 0)) || (this.chartContainer?.nativeElement?.clientWidth) || 400;
    const avgCharWidth = 7; // approximate px per character
    const itemWidthPx = 12; // match mixed chart color box size
    const itemGap = 16; // match mixed chart legend spacing
    const paddingForEdges = 40; // padding reserve left+right
    const totalLabelChars = this.series.reduce((acc, s) => acc + (s.name ? s.name.length : 0), 0);
    const estimatedLegendWidth = this.series.length * (itemWidthPx + itemGap) + totalLabelChars * avgCharWidth + paddingForEdges;
    const legendIsCentered = estimatedLegendWidth < chartWidth;
    const legendObj: any = {
      show: this.showLegend,
      type: 'scroll',
      orient: 'horizontal',
      top: '5%',
      textStyle: { fontSize: sc(11) },
      itemGap: sc(itemGap),
      itemWidth: sc(itemWidthPx),
      itemHeight: sc(itemWidthPx)
    };
    // preserve user legend selection state across renders if available
    try {
      const currentOpt: any = this.chart?.getOption?.() as any;
      const existingSelected = currentOpt?.legend?.[0]?.selected || {};
      // only apply if we have a non-empty map
      if (existingSelected && Object.keys(existingSelected).length) {
        legendObj.selected = { ...existingSelected };
      }
    } catch (e) {
      // ignore if chart not initialized yet
    }
    if (legendIsCentered) {
      legendObj.left = 'center';
    } else {
      legendObj.right = 10;
    }

    // prepare visible secondary labels aligned with visibleCategories
    const visibleSecondary = (this.secondaryLabels && this.secondaryLabels.length)
      ? this.secondaryLabels.slice(startIdx, endIdx)
      : [];

    const secondaryKeyName = this.data?.secondaryLabelKey || '';

    // Compact number formatter for K/M display, shared by tooltip and labels
    const formatCompact = (n: number) => {
      if (n === null || n === undefined || !isFinite(n)) return '';
      const abs = Math.abs(n);
      if (abs >= 1000000) {
        const val = Math.round(n / 100000) / 10; // one decimal precision
        return `${val % 1 === 0 ? val.toFixed(0) : val.toFixed(1)}M`;
      }
      if (abs >= 1000) {
        const val = Math.round(n / 100) / 10; // one decimal precision
        return `${val % 1 === 0 ? val.toFixed(0) : val.toFixed(1)}K`;
      }
      return n.toLocaleString('en-US');
    };
    const fmtNumber = (v: any) => (typeof v === 'number' ? formatCompact(v) : v);
    const showLabels = this.data?.chartConfig?.showLabels !== false && this.data?.chartConfig?.showDataLabels !== false;
    const showTooltip = this.data?.chartConfig?.tooltip?.show !== false;

    const option: echarts.EChartsOption = {
      tooltip: {
        show: showTooltip,
        // show all series values for the hovered category, stacked vertically
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
        position: (pos: any) => [pos[0], Math.max(pos[1] - 12, 8)],
        formatter: (params: any) => {
          if (!showTooltip) return '';
          if (!params) return '';
          const items = Array.isArray(params) ? params : [params];
          const idx = items[0]?.dataIndex ?? 0;
          const humanize = (s: any) => {
            if (s === null || s === undefined) return '';
            return String(s)
              .replace(/_/g, ' ')
              .split(' ')
              .map((w: string) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
              .join(' ');
          };

          const categoryKeyName = humanize(this.data?.categoryKey || 'Category');
          const categoryValue = items[0]?.name ?? '';
          const lines: string[] = [];
          // Category line first
          lines.push(`${categoryKeyName}: ${categoryValue}`);
          // Then each series/value on its own line
          const quantityKey = this.data?.quantityKey;
            items.forEach((p: any) => {
            const name = humanize(p.seriesName || '');
            const value = p.value == null ? null : p.value;
            const numeric = value == null ? NaN : Number(value);
            if (!isNaN(numeric) && numeric === 0) return;
            const valueStr = value !== null ? `${value}${showPercentage ? '%' : ''}` : '';
            // try to find raw row for this category/series to surface quantity if present
            let qtySuffix = '';
            if (quantityKey && this.lastRawBar && this.lastRawBar.length) {
              const found = this.lastRawBar.find((r: any) => String(this.safeGetValue(r, this.data.categoryKey) ?? '') === String(categoryValue)
                && String(this.safeGetValue(r, this.data.seriesKey) ?? '') === String(p.seriesName));
              if (found && found[quantityKey] !== undefined) {
                    lines.push(`${humanize(quantityKey)}: ${fmtNumber(found[quantityKey])}`);
              }
            }
            lines.push(`${name}: ${valueStr}${qtySuffix}`);
          });
          // Finally append secondary label if present
          const secondary = (visibleSecondary && visibleSecondary.length && visibleSecondary[idx]) ? visibleSecondary[idx] : '';
          if (secondary) {
            lines.push(`${secondaryKeyName || 'Detail'}: ${secondary}`);
          }
          // Use HTML line breaks so tooltip displays stacked rows
          return lines.join('<br/>');
        }
      },
      legend: legendObj,
      grid: {
        show: false,
        left: '2%',
        right: '18%',
        top: '15%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        min: 0,
        max: 100,
        splitLine: { show: false },
        axisLine: { show: true },
        axisLabel: {
          show: showLabels,
          formatter: (value: number) => {
            return `${value}%`;
          }
        },
        interval: 100
      },
           // yAxis built into an array of any to satisfy typing
      yAxis: [{
        type: 'category',
        inverse: true,
        data: this.visibleCategories,
        splitLine: { show: false },
        axisLabel: {
          show: true,
          color: theme.chart?.textColor || '#333',
          fontSize: sc(10),
          align: 'right',
          interval: 0,
          formatter: (value: string, index: number) => {
            const secondary = visibleSecondary[index] ? visibleSecondary[index] : '';
            return secondary ? `${value}\n${secondary}` : value;
          }
        }
      }],
        series: visibleSeries.map(s => ({
        name: s.name,
        type: 'bar',
        barGap: '20%',
        label: {
          show: showLabels,
          position: 'right',
          fontSize: sc(10),
          color: theme.chart?.textColor || '#333',
          formatter: (params: any) => {
            const val = params?.value;
            if (val == null || val === 0) return '';
            if (this.showPercentage) {
              return `${val}%`;
            }
            const display = (typeof val === 'number') ? formatCompact(val) : val;
            return `${display}`;
          }
        },
        itemStyle: {
          color: s.color
        },
        data: s.data
      }))
    };
    // attach computed legend object
    (option as any).legend = legendObj;

    console.log('ClusteredBar: Setting chart option with', this.visibleCategories.length, 'visible categories');
    applyRoleVisibilityToOption(option, this.data);
    this.chart.setOption(option, true);
    // Ensure chart is resized to current container size
    setTimeout(() => this.chart?.resize(), 0);
    this.setupChartClickEvent();
    // Re-apply active visual selection after re-render
    if (this.selectedCategories.length > 0) {
      this.highlightCategories(this.selectedCategories);
    } else if (this.selectedLegends.length > 0) {
      this.highlightSeriesByNames(this.selectedLegends);
    }
    // ensure ECharts resizes to actual container after option applied
    setTimeout(() => {
      try { this.chart.resize(); } catch (e) { /* ignore */ }
      this.chart.hideLoading();
    }, 0);
  }

  protected setupChartClickEvent(): void {
    this.chart.off('click');
    this.chart.on('click', (params: any) => {
      // Handle y-axis label clicks (categories)
      if (params.componentType === 'yAxis') {
        if (this.data?.enableLabelClick) {
          this.handleLabelClick(params.value);
        }
        return;
      }

      if (!params.name) return;

      // Bar click → treat as label click if enableLabelClick
      if (this.data?.enableLabelClick) {
        this.handleLabelClick(params.name);
        return;
      }

      // Fallback: original filter selection behavior
      const clickedCategory = params.name;
      const clickedSeriesName = params.seriesName;

      if (this.selectedCategory === clickedCategory && this.selectedSeriesName === clickedSeriesName) {
        this.selectedSeriesName = null;
        this.selectedCategory = null;
        // this.chartFilterHandler.resetFilterSelection(this.data);
        // Reload self with current filters (chart-click filters removed)
        this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
        return;
      }

      this.selectedCategory = clickedCategory;
      this.selectedSeriesName = clickedSeriesName;

      const filterKeys = (this.data.filterKey || '').split(',').map((k: string) => k.trim());
      const values: string[] = [];
      
      // For each filter key, try to find the corresponding value
      filterKeys.forEach((key: string) => {
        if (key === this.data.categoryKey) {
          values.push(clickedCategory);
        } 
        // If there's a seriesKey and it matches, use the clicked series name
        else if (this.data.seriesKey && key === this.data.seriesKey) {
          values.push(clickedSeriesName || '');
        } else if (key === this.data.secondaryLabelKey) {
          values.push(clickedCategory);
        } else {
          values.push('');
        }
      });

      this.chartFilterHandler.updateFilterSelection(this.data, values);
      this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
    });

    // Handle ECharts built-in legend click
    this.chart.off('legendselectchanged');
    this.chart.on('legendselectchanged', (params: any) => {
      if (!this.data?.enableLegendClick) return;
      // Prevent ECharts default legend toggle
      const selected: any = {};
      Object.keys(params.selected).forEach(k => selected[k] = true);
      this.chart.setOption({ legend: { selected } });
      this.onLegendItemClick(params.name);
    });
  }

  // ==================== CROSS-FILTERING METHODS ====================

  /**
   * Called when user clicks a legend item.
   */
  onLegendItemClick(legendName: string): void {
    if (!this.data?.enableLegendClick) return;

    if (this.data?.isMultiSelect) {
      const idx = this.selectedLegends.indexOf(legendName);
      if (idx === -1) {
        this.selectedLegends = [...this.selectedLegends, legendName];
      } else {
        this.selectedLegends = this.selectedLegends.filter(l => l !== legendName);
      }
    } else {
      if (this.selectedLegends.length === 1 && this.selectedLegends[0] === legendName) {
        this.selectedLegends = [];
      } else {
        this.selectedLegends = [legendName];
      }
    }

    if (this.selectedLegends.length === 0) {
      this.broadcastLegendFilter([]);
    } else {
      this.broadcastLegendFilter(this.selectedLegends);
    }

    this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
  }

  /**
   * Called when user clicks a y-axis label (category) or bar.
   */
  protected handleLabelClick(clickedCategory: string): void {
    if (!clickedCategory || !this.data?.enableLabelClick) return;
    const clickedStr = String(clickedCategory);

    if (this.data?.isMultiSelect) {
      const idx = this.selectedCategories.findIndex(c => String(c) === clickedStr);
      if (idx === -1) {
        this.selectedCategories = [...this.selectedCategories, clickedStr];
      } else {
        this.selectedCategories = this.selectedCategories.filter(c => String(c) !== clickedStr);
      }
    } else {
      if (this.selectedCategories.length === 1 && String(this.selectedCategories[0]) === clickedStr) {
        this.selectedCategories = [];
      } else {
        this.selectedCategories = [clickedStr];
      }
    }

    if (this.selectedCategories.length === 0) {
      this.broadcastCategoryFilter([]);
    } else {
      this.broadcastCategoryFilter(this.selectedCategories);
    }

    this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
  }

  /**
   * Broadcast legend selection: legendSync for dimming + filter update for API calls.
   */
  protected broadcastLegendFilter(legends: string[]): void {
    const listenerConfig =
      this.data?.legendListener ||
      this.data?.legendListner ||
      this.data?.filterListener ||
      this.data?.filterListner;

    const legendKey = this.data?.legendKey || this.data?.filterKey;

    // Step 1: legendSync for visual dimming
    const allSeriesNames = this.series.map(s => s.name);
    const selectedIndices = legends.map(l => allSeriesNames.indexOf(l)).filter(i => i !== -1);
    const selectedColors = legends
      .map(l => this.series.find(s => s.name === l)?.color || '')
      .filter(Boolean);
    const colorSyncEnabled = this.data?.chartConfig?.colorSyncEnabled === true;

    const legendSyncData = buildLegendSyncData(
      selectedIndices,
      allSeriesNames,
      this.data.widgetId,
      listenerConfig,
      { colorSyncEnabled, selectedColors, legendKey },
    );
    (this.globalFilterService as any).filterSubject.next(legendSyncData);

    // Step 2: regular filter update for API calls
    if (legendKey) {
      if (legends.length > 0) {
        this.globalFilterService.updateFilterSelection(
          legendKey, legends, false, this.data.widgetId, true, listenerConfig,
        );
      } else {
        this.globalFilterService.resetFilterSelection(legendKey, false, listenerConfig);
      }
    }
  }

  /**
   * Broadcast category (label) selection: legendSync for dimming + filter update for API calls.
   */
  protected broadcastCategoryFilter(categories: string[]): void {
    const listenerConfig =
      this.data?.labelListener ||
      this.data?.legendListener ||
      this.data?.filterListener ||
      this.data?.filterListner;

    const labelKey = this.data?.labelKey || this.data?.filterKey;

    // Step 1: legendSync for visual dimming
    const legendSyncData = {
      ...this.globalFilterService.getCurrentFilters(),
      ...buildCategoryFilterData(categories, this.data.widgetId, listenerConfig, labelKey),
    };
    (this.globalFilterService as any).filterSubject.next(legendSyncData);

    // Step 2: regular filter update for API calls
    if (labelKey) {
      if (categories.length > 0) {
        this.globalFilterService.updateFilterSelection(
          labelKey, categories, false, this.data.widgetId, true, listenerConfig,
        );
      } else {
        this.globalFilterService.resetFilterSelection(labelKey, false, listenerConfig);
      }
    }
  }

  /**
   * Handle incoming legendSync from another chart.
   */
  protected handleExternalLegendSync(filters: any): void {
    if (filters.sourceWidgetId === this.data?.widgetId) return;
    if (!this.chart) return;

    if (filters.selectedLegends?.length === 0 && filters.selectedIndices?.length === 0) {
      this.selectedCategories = [];
      this.selectedLegends = [];
      this.resetHighlight();
      return;
    }

    if (filters.isCategoryFilter && filters.selectedLegends?.length > 0) {
      const matchMode = (this.data?.chartConfig?.categoryMatchMode || 'contains') as MatchMode;
      const matchingCategories = this.categories.filter((cat: string) =>
        filters.selectedLegends.some((legend: string) => matchValue(cat, legend, matchMode))
      );
      if (matchingCategories.length > 0) {
        this.selectedCategories = matchingCategories;
        this.highlightCategories(matchingCategories);
      }
    } else if (filters.selectedLegends?.length > 0) {
      const seriesNames = this.series.map(s => s.name);
      const matchMode = (this.data?.chartConfig?.categoryMatchMode || 'contains') as MatchMode;
      const matchingSeries = seriesNames.filter((name: string) =>
        filters.selectedLegends.some((legend: string) => matchValue(name, legend, matchMode))
      );
      if (matchingSeries.length > 0) {
        this.highlightSeriesByNames(matchingSeries);
      } else {
        const matchingCategories = this.categories.filter((cat: string) =>
          filters.selectedLegends.some((legend: string) => matchValue(cat, legend, matchMode))
        );
        if (matchingCategories.length > 0) {
          this.highlightCategories(matchingCategories);
        }
      }
    }
  }

  // ==================== VISUAL HIGHLIGHTING ====================

  /**
   * Dim all categories except the selected ones.
   * This is a horizontal chart with inverse yAxis, so categories are reversed for display.
   */
  protected highlightCategories(categories: string[]): void {
    if (!this.chart) return;
    const option = this.chart.getOption() as any;
    if (!option?.series) return;

    // visibleCategories are NOT reversed here, but the chart displays them with inverse:true
    // So data index 0 = last visible category visually, but data arrays match visibleCategories order
    const matchIndices = categories
      .map((cat: string) => this.visibleCategories.findIndex(c => String(c) === String(cat)))
      .filter((i: number) => i !== -1);

    const updatedSeries = option.series.map((s: any) => {
      const seriesName = Array.isArray(s.name) ? s.name[0] : s.name;
      if (seriesName === 'Total') return s;
      if (!s.data) return s;
      const dataArr = Array.isArray(s.data[0]) ? s.data[0] : s.data;
      const newData = dataArr.map((val: any, idx: number) => {
        const isMatch = matchIndices.includes(idx);
        const opacity = isMatch ? 1 : 0.2;
        if (typeof val === 'object' && val !== null) {
          return { ...val, itemStyle: { ...(val.itemStyle || {}), opacity } };
        }
        return { value: val, itemStyle: { opacity } };
      });
      return { ...s, data: newData };
    });

    this.chart.setOption({ series: updatedSeries });
  }

  /**
   * Dim all series except those whose names are in the given list.
   */
  protected highlightSeriesByNames(names: string[]): void {
    if (!this.chart) return;
    const option = this.chart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      const seriesName = Array.isArray(s.name) ? s.name[0] : s.name;
      if (seriesName === 'Total') return s;
      const seriesType = Array.isArray(s.type) ? s.type[0] : s.type;
      if (seriesType === 'bar') {
        return { ...s, itemStyle: { ...(Array.isArray(s.itemStyle) ? s.itemStyle[0] : s.itemStyle), opacity: names.includes(seriesName) ? 1 : 0.2 } };
      }
      return s;
    });

    this.chart.setOption({ series: updatedSeries });
  }

  /**
   * Reset all visual highlighting.
   */
  protected resetHighlight(): void {
    if (!this.chart) return;
    const option = this.chart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      const seriesName = Array.isArray(s.name) ? s.name[0] : s.name;
      if (seriesName === 'Total') return s;
      const seriesType = Array.isArray(s.type) ? s.type[0] : s.type;
      if (seriesType === 'bar') {
        if (!s.data) return { ...s, itemStyle: { ...(Array.isArray(s.itemStyle) ? s.itemStyle[0] : s.itemStyle), opacity: 1 } };
        const dataArr = Array.isArray(s.data[0]) ? s.data[0] : s.data;
        const newData = dataArr.map((val: any) => {
          if (typeof val === 'object' && val !== null) {
            return { ...val, itemStyle: { ...(val.itemStyle || {}), opacity: 1 } };
          }
          return { value: val, itemStyle: { opacity: 1 } };
        });
        return { ...s, itemStyle: { ...(Array.isArray(s.itemStyle) ? s.itemStyle[0] : s.itemStyle), opacity: 1 }, data: newData };
      }
      return s;
    });

    this.chart.setOption({ series: updatedSeries });
  }

  onLabelsScroll(event: Event): void {
    // Sync chart scroll with labels scroll
    const labelsDiv = event.target as HTMLDivElement;
    const scrollTop = labelsDiv.scrollTop;
    const rowsScrolled = Math.floor(scrollTop / this.rowHeight);
const newOffset = rowsScrolled;
    if (newOffset !== this.scrollOffset) {
      this.scrollOffset = newOffset;
      this.renderChart();
    }
  }

  onScrollbarMouseDown(event: MouseEvent): void {
    event.preventDefault();
    if (this.categories.length <= this.maxVisibleCategories) return;
    
    this.isScrollbarDragging = true;
    this.scrollbarStartY = event.clientY;
    this.scrollStartOffset = this.scrollOffset;
    
    // Create bound handlers
    this.dragHandler = (e: MouseEvent) => this.onScrollbarDrag(e);
    this.upHandler = () => this.onScrollbarMouseUp();
    
    document.addEventListener('mousemove', this.dragHandler!);
    document.addEventListener('mouseup', this.upHandler!);
  }

  getScrollbarThumbHeight(): number {
    if (this.categories.length <= this.maxVisibleCategories) return 0;
    return (this.maxVisibleCategories / this.categories.length) * this.visibleHeight;
  }

  getScrollbarThumbTop(): number {
    if (this.categories.length <= this.maxVisibleCategories) return 0;
    const maxScroll = this.categories.length - this.maxVisibleCategories;
    const scrollableHeight = this.visibleHeight;
    const thumbHeight = this.getScrollbarThumbHeight();
    const maxThumbTravel = scrollableHeight - thumbHeight;
    return (this.scrollOffset / maxScroll) * maxThumbTravel;
  }

  protected onScrollbarDrag(event: MouseEvent): void {
    if (!this.isScrollbarDragging) return;
    
    const deltaY = event.clientY - this.scrollbarStartY;
    const maxScroll = this.categories.length - this.maxVisibleCategories;
    const scrollableHeight = this.visibleHeight;
    const thumbHeight = (this.maxVisibleCategories / this.categories.length) * scrollableHeight;
    const maxThumbTravel = scrollableHeight - thumbHeight;
    
    if (maxThumbTravel <= 0) return;
    
    // Calculate new offset based on thumb position
    const newThumbY = this.scrollStartOffset * thumbHeight / maxScroll + deltaY;
    const clampedThumbY = Math.max(0, Math.min(newThumbY, maxThumbTravel));
    const newOffset = Math.round((clampedThumbY / maxThumbTravel) * maxScroll);
    
    if (newOffset !== this.scrollOffset && newOffset >= 0 && newOffset <= maxScroll) {
      this.scrollOffset = newOffset;
      this.renderChart();
    }
  }

  protected onScrollbarMouseUp(): void {
    this.isScrollbarDragging = false;
    if (this.dragHandler) {
      document.removeEventListener('mousemove', this.dragHandler);
      this.dragHandler = null;
    }
    if (this.upHandler) {
      document.removeEventListener('mouseup', this.upHandler);
      this.upHandler = null;
    }
  }

  private extractFlatRows(response: any): any[] {
    const candidates = [
      Array.isArray(response) ? response : null,
      response?.responseList?.[0]?.data,
      response?.data,
    ];
    for (const candidate of candidates) {
      if (Array.isArray(candidate) && candidate.length > 0) return candidate;
    }
    return [];
  }

  protected transformData(
  rawData: any[],
  categoryKey: string,
  seriesKey: string,
  valueKey: string
): { category: string[]; series: ClusteredBarSeries[] } {

  if (!rawData || !rawData.length) {
    return { category: [], series: [] };
  }

  const safeGet = (obj: any, key: string) => {
    if (!obj || !key) return undefined;
    if (obj.hasOwnProperty(key)) return obj[key];

    const lower = key.toLowerCase();
    const found = Object.keys(obj).find(k => k.toLowerCase() === lower);
    return found ? obj[found] : undefined;
  };

  const getVal = (obj: any, key: string) => {
    const v = safeGet(obj, key);
    return v === undefined || v === null ? 0 : Number(v);
  };

  const getCat = (obj: any) => {
    const v = safeGet(obj, categoryKey);
    return v === undefined || v === null ? '' : String(v);
  };

  // 1️⃣ Get unique categories
  const uniqueCategories = Array.from(
    new Set(rawData.map(d => getCat(d)))
  ).filter(c => c !== '');

  // 2️⃣ Calculate total per category
  const categoryTotals = uniqueCategories.map(cat => {
    const total = rawData
      .filter(d => getCat(d) === cat)
      .reduce((sum, row) => sum + getVal(row, valueKey), 0);

    return { category: cat, total };
  });

  // 3️⃣ Sort DESCENDING
  categoryTotals.sort((a, b) => b.total - a.total);

  const sortedCategories = categoryTotals.map(c => c.category);

  // 4️⃣ If no seriesKey → single series mode
  if (!seriesKey) {
    const singleData = sortedCategories.map(cat => {
      return rawData
        .filter(d => getCat(d) === cat)
        .reduce((sum, row) => sum + getVal(row, valueKey), 0);
    });

    return {
      category: sortedCategories,
      series: [{
        name: this.data?.chartConfig?.singleSeriesName || 'Value',
        data: singleData
      }]
    };
  }

  // 5️⃣ Multi-series mode
  const seriesNames = Array.from(
    new Set(rawData.map(d => String(safeGet(d, seriesKey) ?? '')))
  ).filter(s => s !== '');

  const series = seriesNames.map(name => {
    const data = sortedCategories.map(cat => {
      return rawData
        .filter(d =>
          getCat(d) === cat &&
          String(safeGet(d, seriesKey) ?? '') === name
        )
        .reduce((sum, row) => sum + getVal(row, valueKey), 0);
    });

    return { name, data };
  });

  return { category: sortedCategories, series };
}

  protected syncVisibleHeightFromContainer(): void {
    const actualHeight = this.chartContainer?.nativeElement?.clientHeight;
    if (actualHeight && actualHeight > 0) {
      this.visibleHeight = actualHeight;
    }
  }
}
